import { jsPDF } from "jspdf"
import type { TFunction } from "i18next"

import type { IntakeLoad } from "@/features/intake/api/types"

const PAGE = { margin: 10, width: 210, height: 297 }
const INK: [number, number, number] = [33, 33, 33]
const MUTED: [number, number, number] = [110, 110, 115]
const LINE: [number, number, number] = [160, 160, 160]
const SECTION_BG: [number, number, number] = [224, 224, 224]
const HEADER_BG: [number, number, number] = [250, 250, 250]
const SUB_BG: [number, number, number] = [238, 238, 238]
const ACCENT: [number, number, number] = [33, 33, 33]

interface FormCell {
  label?: string
  value?: string | number | null
}

type FormRow = [FormCell, FormCell]

interface FormGridOpts {
  labelW?: number
  rowH?: number
}

function groupThousands(s: string): string {
  // Group only the integer part — keep the decimal half intact.
  const [intPart, decPart] = s.split(".")
  const grouped = intPart.replace(/\B(?=(\d{3})+(?!\d))/g, " ")
  return decPart ? `${grouped},${decPart}` : grouped
}

// Print kg with 2 decimal places — same precision the backend stores
// at, so the bordereau matches the persisted value byte-for-byte.
// French-locale "," is used as the decimal separator since the
// bordereau is a French-language document.
function fmtKg(n: number | null | undefined): string {
  if (n == null || Number.isNaN(n)) return "—"
  return groupThousands(n.toFixed(2))
}

// FCFA is integer-only (no minor units in the field).
function fmtFcfa(n: number | null | undefined): string {
  if (n == null || Number.isNaN(n)) return "—"
  return groupThousands(String(Math.round(n)))
}

function fmtPct(n: number | null | undefined): string {
  if (n == null || Number.isNaN(n)) return "—"
  return Number(n).toFixed(2).replace(".", ",")
}

let logoPngPromise: Promise<string | null> | null = null
async function loadAbeyaLogoPng(): Promise<string | null> {
  if (typeof window === "undefined") return null
  if (logoPngPromise) return logoPngPromise
  logoPngPromise = (async () => {
    try {
      const res = await fetch("/abeya-indigo-logo.svg")
      if (!res.ok) return null
      const raw = await res.text()
      const svg = raw.replace(/#6610F2/gi, "#111111")
      const blob = new Blob([svg], { type: "image/svg+xml" })
      const url = URL.createObjectURL(blob)
      const img = new Image()
      await new Promise<void>((resolve, reject) => {
        img.onload = () => resolve()
        img.onerror = () => reject(new Error("logo decode failed"))
        img.src = url
      })
      const canvas = document.createElement("canvas")
      const scale = 3
      canvas.width = 811 * scale
      canvas.height = 347 * scale
      const ctx = canvas.getContext("2d")
      if (!ctx) {
        URL.revokeObjectURL(url)
        return null
      }
      ctx.drawImage(img, 0, 0, canvas.width, canvas.height)
      URL.revokeObjectURL(url)
      return canvas.toDataURL("image/png")
    } catch {
      return null
    }
  })()
  return logoPngPromise
}

function sectionHeader(doc: jsPDF, y: number, title: string): number {
  const x = PAGE.margin
  const w = PAGE.width - PAGE.margin * 2
  doc.setFillColor(...SECTION_BG)
  doc.setDrawColor(...LINE)
  doc.setLineWidth(0.2)
  doc.rect(x, y, w, 5.5, "FD")
  doc.setFont("helvetica", "bold")
  doc.setFontSize(8.5)
  doc.setTextColor(...INK)
  doc.text(title, x + 2.5, y + 3.8)
  return y + 5.5
}

function subHeader(doc: jsPDF, y: number, title: string): number {
  const x = PAGE.margin
  const w = PAGE.width - PAGE.margin * 2
  doc.setFillColor(...SUB_BG)
  doc.setDrawColor(...LINE)
  doc.setLineWidth(0.2)
  doc.rect(x, y, w, 5, "FD")
  doc.setFont("helvetica", "bolditalic")
  doc.setFontSize(7.8)
  doc.setTextColor(...INK)
  doc.text(title, x + 2.5, y + 3.5)
  return y + 5
}

function formGrid(
  doc: jsPDF,
  y: number,
  rows: FormRow[],
  opts: FormGridOpts = {},
): number {
  const x = PAGE.margin
  const fullW = PAGE.width - PAGE.margin * 2
  const labelW = opts.labelW ?? 42
  const pair = fullW / 2
  const valueW = pair - labelW
  const rowH = opts.rowH ?? 6

  doc.setDrawColor(...LINE)
  doc.setLineWidth(0.2)

  rows.forEach((row, i) => {
    const ry = y + i * rowH
    const cells = [
      { x, w: labelW, label: row[0]?.label, value: null, isLabel: true },
      {
        x: x + labelW,
        w: valueW,
        label: null,
        value: row[0]?.value,
        isLabel: false,
      },
      { x: x + pair, w: labelW, label: row[1]?.label, value: null, isLabel: true },
      {
        x: x + pair + labelW,
        w: valueW,
        label: null,
        value: row[1]?.value,
        isLabel: false,
      },
    ]

    cells.forEach((c) => {
      if (c.isLabel && c.label) {
        doc.setFillColor(...HEADER_BG)
        doc.rect(c.x, ry, c.w, rowH, "FD")
        doc.setFont("helvetica", "bold")
        doc.setFontSize(8)
        doc.setTextColor(...INK)
        const lines = doc.splitTextToSize(c.label, c.w - 3)
        doc.text(lines, c.x + 2, ry + (lines.length === 1 ? 4 : 3.2))
      } else if (!c.isLabel) {
        doc.setFillColor(255, 255, 255)
        doc.rect(c.x, ry, c.w, rowH, "FD")
        if (c.value != null && c.value !== "") {
          doc.setFont("helvetica", "normal")
          doc.setFontSize(8.5)
          doc.setTextColor(...INK)
          const lines = doc.splitTextToSize(String(c.value), c.w - 3)
          doc.text(lines, c.x + 2, ry + (lines.length === 1 ? 4 : 3.2))
        }
      } else {
        doc.setFillColor(...HEADER_BG)
        doc.rect(c.x, ry, c.w, rowH, "FD")
      }
    })
  })

  return y + rows.length * rowH
}

function formGridSingle(
  doc: jsPDF,
  y: number,
  rows: FormCell[],
  opts: FormGridOpts = {},
): number {
  const x = PAGE.margin
  const fullW = PAGE.width - PAGE.margin * 2
  const labelW = opts.labelW ?? 42
  const valueW = fullW - labelW
  const rowH = opts.rowH ?? 6
  doc.setDrawColor(...LINE)
  doc.setLineWidth(0.2)

  rows.forEach((r, i) => {
    const ry = y + i * rowH
    doc.setFillColor(...HEADER_BG)
    doc.rect(x, ry, labelW, rowH, "FD")
    doc.setFillColor(255, 255, 255)
    doc.rect(x + labelW, ry, valueW, rowH, "FD")
    doc.setFont("helvetica", "bold")
    doc.setFontSize(8)
    doc.setTextColor(...INK)
    if (r.label) doc.text(r.label, x + 2, ry + 4)
    doc.setFont("helvetica", "normal")
    doc.setFontSize(8.5)
    if (r.value != null && r.value !== "") {
      const lines = doc.splitTextToSize(String(r.value), valueW - 3)
      doc.text(lines, x + labelW + 2, ry + (lines.length === 1 ? 4 : 3.2))
    }
  })

  return y + rows.length * rowH
}

function drawTitleBanner(
  doc: jsPDF,
  title: string,
  logoDataUrl: string | null,
  subtitle: string,
): number {
  const x = PAGE.margin
  const y = PAGE.margin
  const w = PAGE.width - PAGE.margin * 2
  const h = 18

  doc.setDrawColor(...LINE)
  doc.setLineWidth(0.3)
  doc.setFillColor(255, 255, 255)
  doc.rect(x, y, w, h, "FD")

  const logoW = 45
  doc.setFillColor(252, 252, 253)
  doc.rect(x, y, logoW, h, "FD")

  if (logoDataUrl) {
    try {
      const maxW = 18
      const maxH = 7.5
      const ratio = 811 / 347
      let dw = maxW
      let dh = dw / ratio
      if (dh > maxH) {
        dh = maxH
        dw = dh * ratio
      }
      const lx = x + (logoW - dw) / 2
      const ly = y + (h - dh) / 2
      doc.addImage(logoDataUrl, "PNG", lx, ly, dw, dh, undefined, "FAST")
    } catch {
      doc.setFont("helvetica", "bold")
      doc.setFontSize(10)
      doc.setTextColor(...ACCENT)
      doc.text("abeya", x + logoW / 2, y + h / 2 + 1.2, { align: "center" })
    }
  } else {
    doc.setFont("helvetica", "bold")
    doc.setFontSize(10)
    doc.setTextColor(...ACCENT)
    doc.text("abeya", x + logoW / 2, y + h / 2 + 1.2, { align: "center" })
  }

  doc.setFont("helvetica", "bold")
  doc.setFontSize(14)
  doc.setTextColor(...INK)
  const titleCenterX = x + logoW + (w - logoW) / 2
  doc.text(title, titleCenterX, y + (subtitle ? 10 : 11.5), { align: "center" })
  if (subtitle) {
    doc.setFont("helvetica", "italic")
    doc.setFontSize(8)
    doc.setTextColor(...MUTED)
    doc.text(subtitle, titleCenterX, y + 14.5, { align: "center" })
  }

  return y + h
}

function drawFooter(
  doc: jsPDF,
  t: TFunction,
  number: string,
  loadId: string | number,
): void {
  const fy = 287
  doc.setDrawColor(...LINE)
  doc.setLineWidth(0.2)
  doc.line(PAGE.margin, fy - 3, PAGE.width - PAGE.margin, fy - 3)
  doc.setFont("helvetica", "bold")
  doc.setFontSize(7)
  doc.setTextColor(...INK)
  doc.text("Abeya", PAGE.margin, fy + 1)
  doc.setFont("helvetica", "normal")
  doc.setTextColor(...MUTED)
  doc.text(" · " + t("pdf.footer.poweredBy"), PAGE.margin + 8.5, fy + 1)
  doc.text(
    t("pdf.footer.slipMeta", { number, id: loadId }),
    PAGE.width - PAGE.margin,
    fy + 1,
    { align: "right" },
  )
}

interface BuildOpts {
  logoDataUrl?: string | null
}

function isoDatePart(value: string | null | undefined): string {
  if (!value) return "—"
  const d = new Date(value)
  if (Number.isNaN(d.getTime())) return value.slice(0, 10)
  return d.toISOString().slice(0, 10)
}

function isoTimePart(value: string | null | undefined): string {
  if (!value) return "—"
  const d = new Date(value)
  if (Number.isNaN(d.getTime())) return "—"
  return d.toISOString().slice(11, 16)
}

export function buildBordereauPdf(
  load: IntakeLoad,
  t: TFunction,
  opts: BuildOpts = {},
): jsPDF {
  const doc = new jsPDF({ unit: "mm", format: "a4" })
  const lab = load.labResults ?? null
  const sampling = load.sampling ?? null
  const cooperative = load.Orders?.groupId?.name ?? "—"
  const slip = load.DeliveryForms?.billOfLadingNumber ?? "—"
  const number =
    load.bordereauNumber != null ? String(load.bordereauNumber) : "—"

  let y = drawTitleBanner(doc, t("pdf.title"), opts.logoDataUrl ?? null, t("pdf.subtitle"))

  const metaRows: FormRow[] = [
    [
      { label: t("pdf.meta.campaign"), value: "2025-2026" },
      { label: t("pdf.meta.bordereauNo"), value: number },
    ],
    [
      { label: t("pdf.meta.product"), value: t("pdf.meta.productValue") },
      {
        label: t("pdf.meta.issuedAt"),
        value: isoDatePart(load.bordereauIssuedAt ?? load.arrivedAt),
      },
    ],
    [
      { label: t("pdf.meta.code"), value: load.id },
      { label: t("pdf.meta.connaissement"), value: slip },
    ],
  ]
  y = formGrid(doc, y, metaRows, { labelW: 32 })
  y += 0.8

  y = sectionHeader(doc, y, t("pdf.sections.supplier"))
  y = formGrid(doc, y, [
    [
      { label: t("pdf.supplier.name"), value: cooperative },
      { label: t("pdf.supplier.agrement"), value: "—" },
    ],
    [
      { label: t("pdf.supplier.location"), value: "—" },
      { label: t("pdf.supplier.phone"), value: "—" },
    ],
  ])
  y += 0.8

  y = sectionHeader(doc, y, t("pdf.sections.destination"))
  y = formGrid(doc, y, [
    [
      { label: t("pdf.destination.operatorType"), value: "—" },
      { label: t("pdf.destination.postalAddress"), value: "—" },
    ],
    [
      { label: t("pdf.destination.code"), value: load.id },
      { label: t("pdf.destination.phone"), value: "—" },
    ],
  ])
  y += 0.8

  y = sectionHeader(doc, y, t("pdf.sections.chargement"))
  // Average per delivered bag (pre-sondage). The bordereau is the
  // gate-stage receipt so it reports what the truck delivered, not
  // what survived sampling.
  const avgWeight =
    load.bagsUnloaded && load.bagsUnloaded > 0 && load.netWeight
      ? t("pdf.units.kgValue", {
          v: (load.netWeight / load.bagsUnloaded).toFixed(2),
        })
      : "—"
  const plate =
    load.plateNumber ?? load.DeliveryForms?.truckRegistrationNumber ?? "—"
  y = formGrid(doc, y, [
    [
      { label: t("pdf.chargement.origin"), value: "—" },
      { label: t("pdf.chargement.tractorNo"), value: plate },
    ],
    [
      {
        label: t("pdf.chargement.departureDate"),
        value: isoDatePart(load.arrivedAt),
      },
      { label: t("pdf.chargement.vehicleNo"), value: plate },
    ],
    [
      {
        label: t("pdf.chargement.arrivalDate"),
        value: isoDatePart(load.arrivedAt),
      },
      { label: t("pdf.chargement.driver"), value: load.driver ?? "—" },
    ],
    [
      { label: t("pdf.chargement.avgWeight"), value: avgWeight },
      {
        label: t("pdf.chargement.entryTime"),
        value: isoTimePart(load.arrivedAt),
      },
    ],
  ])
  y += 0.8

  y = sectionHeader(doc, y, t("pdf.sections.delivery"))
  y = subHeader(doc, y, t("pdf.sections.quantity"))
  const otherBad = Math.max(
    0,
    (sampling?.bagsRejected ?? 0) - (sampling?.bagsWet ?? 0),
  )
  y = formGrid(doc, y, [
    [
      { label: t("pdf.delivery.bagsUnloaded"), value: load.bagsUnloaded ?? "—" },
      { label: t("pdf.delivery.grossIn"), value: fmtKg(load.grossWeight ?? null) },
    ],
    [
      { label: t("pdf.delivery.tareOut"), value: fmtKg(load.tareWeight ?? null) },
      { label: t("pdf.delivery.netWeight"), value: fmtKg(load.netWeight ?? null) },
    ],
    [
      { label: t("pdf.delivery.bagsRejected"), value: sampling?.bagsRejected ?? 0 },
      { label: t("pdf.delivery.ofWhichWet"), value: sampling?.bagsWet ?? 0 },
    ],
    [
      { label: t("pdf.delivery.otherBad"), value: otherBad },
      {
        label: t("pdf.delivery.wetDeduction"),
        value: fmtKg(load.wetBagDeductionKg ?? null),
      },
    ],
    [
      { label: t("pdf.delivery.bagsAccepted"), value: sampling?.acceptedBags ?? 0 },
      {
        label: t("pdf.delivery.acceptedWeight"),
        value: fmtKg(load.acceptedWeightKg ?? null),
      },
    ],
    [
      { label: "", value: "" },
      {
        label: t("pdf.delivery.netAfterRefraction"),
        value: fmtKg(load.netAfterRefractionKg ?? null),
      },
    ],
  ])

  y = formGridSingle(doc, y, [
    {
      label: t("pdf.delivery.refractionTotal"),
      value: fmtKg(load.labRefractionKg ?? null),
    },
  ])
  y += 0.8

  y = sectionHeader(doc, y, t("pdf.sections.price"))
  y = formGrid(doc, y, [
    [
      {
        label: t("pdf.price.perKg"),
        value: t("pdf.units.fcfa", { v: fmtFcfa(load.priceFcfaPerKg ?? null) }),
      },
      { label: t("pdf.price.paymentMode"), value: t("pdf.price.wireTransfer") },
    ],
    [
      {
        label: t("pdf.price.totalDue"),
        value: t("pdf.units.fcfa", { v: fmtFcfa(load.amountFcfa ?? null) }),
      },
      { label: t("pdf.price.issuerBank"), value: "—" },
    ],
  ])
  y += 0.8

  y = sectionHeader(doc, y, t("pdf.sections.analysis"))
  // Défectueuse = germinated + mites + flat beans (CCC v3). Violette is
  // recorded for traceability but no longer drives the grade.
  const defective =
    (lab?.germinated ?? 0) + (lab?.mites ?? 0) + (lab?.flatBeans ?? 0)
  y = formGrid(doc, y, [
    [
      { label: t("pdf.analysis.grainage"), value: `${fmtPct(lab?.grainage)} %` },
      {
        label: t("pdf.analysis.foreignMatter"),
        value: `${fmtPct(lab?.foreignMatter)} %`,
      },
    ],
    [
      { label: t("pdf.analysis.moisture"), value: `${fmtPct(lab?.moisture)} %` },
      { label: t("pdf.analysis.broken"), value: `${fmtPct(lab?.broken)} %` },
    ],
    [
      { label: t("pdf.analysis.waste"), value: `${fmtPct(lab?.waste)} %` },
      { label: t("pdf.analysis.crabot"), value: `${fmtPct(lab?.crabot)} %` },
    ],
    [
      { label: t("pdf.analysis.mouldy"), value: `${fmtPct(lab?.mouldy)} %` },
      { label: t("pdf.analysis.slaty"), value: `${fmtPct(lab?.slaty)} %` },
    ],
    [
      {
        label: t("pdf.analysis.germinated"),
        value: `${fmtPct(lab?.germinated)} %`,
      },
      { label: t("pdf.analysis.mites"), value: `${fmtPct(lab?.mites)} %` },
    ],
    [
      {
        label: t("pdf.analysis.flatBeans"),
        value: `${fmtPct(lab?.flatBeans)} %`,
      },
      { label: t("pdf.analysis.violette"), value: `${fmtPct(lab?.violette)} %` },
    ],
    [
      {
        label: t("pdf.analysis.defectiveCalc"),
        value: `${fmtPct(defective)} %`,
      },
      {
        label: t("pdf.analysis.gradeLabel"),
        value: load.grade ? t(`grade.${load.grade}`) : "—",
      },
    ],
  ])
  y += 1.5

  const sigH = 14
  const FOOTER_Y = 283
  const FOOTER_RESERVE = 6
  if (y + sigH > FOOTER_Y - FOOTER_RESERVE) {
    drawFooter(doc, t, number, load.id)
    doc.addPage()
    y = PAGE.margin
  }

  const sigX = PAGE.margin
  const sigFullW = PAGE.width - PAGE.margin * 2
  const sigW = sigFullW / 3
  doc.setDrawColor(...LINE)
  doc.setLineWidth(0.2)
  for (let i = 0; i < 3; i++) {
    const cx = sigX + i * sigW
    doc.setFillColor(252, 252, 253)
    doc.rect(cx, y, sigW, sigH, "FD")
    doc.setFont("helvetica", "bold")
    doc.setFontSize(7.5)
    doc.setTextColor(...INK)
    doc.text(t("pdf.signature.heading"), cx + sigW / 2, y + 4, { align: "center" })
    doc.setFont("helvetica", "normal")
    doc.setFontSize(7)
    doc.setTextColor(...MUTED)
    const labels = [
      t("pdf.signature.factoryDirector"),
      t("pdf.signature.exporter"),
      t("pdf.signature.supplier"),
    ]
    doc.text(labels[i], cx + sigW / 2, y + 7.5, { align: "center" })
  }

  drawFooter(doc, t, number, load.id)
  return doc
}

export async function downloadBordereauPdf(
  load: IntakeLoad,
  t: TFunction,
): Promise<string> {
  const logoDataUrl = await loadAbeyaLogoPng()
  const doc = buildBordereauPdf(load, t, { logoDataUrl })
  const number =
    load.bordereauNumber != null ? String(load.bordereauNumber) : String(load.id)
  const fileName = t("pdf.fileName", { number })
  doc.save(fileName)
  return fileName
}
